/* eslint-disable react-refresh/only-export-components, max-lines -- A design-system showcase route
   colocates its demo components with the route export by design: each demo exists to be read
   alongside the prose describing it, and splitting them into sibling files would put the example
   and its explanation in different places. Length is the point here, not bloat. */
import {
  DataTableColumnHeader,
  DimeSeparator,
  DimeTable,
  DimeTableBody,
  DimeTableCell,
  DimeTableHead,
  DimeTableHeader,
  DimeTableRow,
  DimeTreeTable,
  type DimeTreeTableProps,
  type TreeColumnDef,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/tree-table')({
  component: TreeTableDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

interface ProductCategory {
  id: string;
  name: string;
  productCount: number;
  children?: ProductCategory[];
}

// A 4-level category tree — deep enough to actually exercise arbitrary depth, not just "2 levels
// plus one more". Product counts are on the leaves; ancestor counts below are the sum of their
// descendants, matching how a real category tree's rollup would be seeded.
const CATEGORY_TREE: ProductCategory[] = [
  {
    id: 'flower',
    name: 'Flower',
    productCount: 34,
    children: [
      {
        id: 'indica',
        name: 'Indica',
        productCount: 21,
        children: [
          { id: 'og-kush', name: 'OG Kush', productCount: 12 },
          { id: 'purple-punch', name: 'Purple Punch', productCount: 9 },
        ],
      },
      {
        id: 'sativa',
        name: 'Sativa',
        productCount: 13,
        children: [
          {
            id: 'sour-diesel',
            name: 'Sour Diesel',
            productCount: 13,
            children: [
              { id: 'sour-diesel-1oz', name: 'Sour Diesel — 1oz', productCount: 8 },
              { id: 'sour-diesel-eighth', name: 'Sour Diesel — Eighth', productCount: 5 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'edibles',
    name: 'Edibles',
    productCount: 19,
    children: [
      {
        id: 'gummies',
        name: 'Gummies',
        productCount: 14,
        children: [
          { id: 'sour-gummies', name: 'Sour Gummies', productCount: 8 },
          { id: 'fruit-chews', name: 'Fruit Chews', productCount: 6 },
        ],
      },
      { id: 'chocolates', name: 'Chocolates', productCount: 5 },
    ],
  },
  { id: 'concentrates', name: 'Concentrates', productCount: 6 },
];

const CATEGORY_COLUMNS: TreeColumnDef<ProductCategory>[] = [
  { accessorKey: 'name', header: 'Category' },
  {
    accessorKey: 'productCount',
    header: 'Products',
    cell: ({ row }) => (
      <span className="tabular-nums text-muted-foreground">{row.original.productCount}</span>
    ),
  },
];

function BasicDemo() {
  return (
    <DimeTreeTable<ProductCategory>
      columns={CATEGORY_COLUMNS}
      data={CATEGORY_TREE}
      getRowId={(category) => category.id}
      getSubRows={(category) => category.children}
      indentColumnId="name"
    />
  );
}

function DefaultAllExpandedDemo() {
  return (
    <DimeTreeTable<ProductCategory>
      columns={CATEGORY_COLUMNS}
      data={CATEGORY_TREE}
      getRowId={(category) => category.id}
      getSubRows={(category) => category.children}
      indentColumnId="name"
      defaultAllExpanded
    />
  );
}

const SORTABLE_CATEGORY_COLUMNS: TreeColumnDef<ProductCategory>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Category" />,
    meta: { label: 'Category' },
  },
  {
    accessorKey: 'productCount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Products" />,
    cell: ({ row }) => (
      <span className="tabular-nums text-muted-foreground">{row.original.productCount}</span>
    ),
    meta: { label: 'Products' },
  },
];

function SortableDemo() {
  return (
    <DimeTreeTable<ProductCategory>
      columns={SORTABLE_CATEGORY_COLUMNS}
      data={CATEGORY_TREE}
      getRowId={(category) => category.id}
      getSubRows={(category) => category.children}
      indentColumnId="name"
      defaultAllExpanded
    />
  );
}

function SearchDemo() {
  return (
    <DimeTreeTable<ProductCategory>
      columns={CATEGORY_COLUMNS}
      data={CATEGORY_TREE}
      getRowId={(category) => category.id}
      getSubRows={(category) => category.children}
      indentColumnId="name"
      searchColumn="name"
      searchPlaceholder="Search categories"
    />
  );
}

function ToolbarDemo() {
  return (
    <DimeTreeTable<ProductCategory>
      columns={SORTABLE_CATEGORY_COLUMNS}
      data={CATEGORY_TREE}
      getRowId={(category) => category.id}
      getSubRows={(category) => category.children}
      indentColumnId="name"
      searchColumn="name"
      searchPlaceholder="Search categories"
      showViewOptions
      showExpandAll
    />
  );
}

function PickerDemo() {
  const [selectedRowId, setSelectedRowId] = React.useState<string | undefined>('sour-gummies');
  const selectedCategory = React.useMemo(() => {
    const walk = (nodes: ProductCategory[]): ProductCategory | undefined => {
      for (const node of nodes) {
        if (node.id === selectedRowId) return node;
        const foundInChildren = node.children ? walk(node.children) : undefined;
        if (foundInChildren) return foundInChildren;
      }
      return undefined;
    };
    return walk(CATEGORY_TREE);
  }, [selectedRowId]);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">
        Selected:{' '}
        <span data-testid="picker-selected-name" className="font-medium text-foreground">
          {selectedCategory?.name}
        </span>
      </p>
      <DimeTreeTable<ProductCategory>
        columns={CATEGORY_COLUMNS}
        data={CATEGORY_TREE}
        getRowId={(category) => category.id}
        getSubRows={(category) => category.children}
        indentColumnId="name"
        defaultAllExpanded
        selectedRowId={selectedRowId}
        onSelectRow={(category) => setSelectedRowId(category.id)}
      />
    </div>
  );
}

function LoadingDemo() {
  return (
    <DimeTreeTable<ProductCategory>
      columns={CATEGORY_COLUMNS}
      data={[]}
      getRowId={(category) => category.id}
      getSubRows={(category) => category.children}
      indentColumnId="name"
      isLoading
      loadingRowCount={4}
    />
  );
}

// Source of truth is DimeTreeTableProps. The assertion after this array fails to compile if a
// prop is added there without a row here, so the docs table cannot silently fall behind.
const PROP_ROWS = [
  {
    prop: 'columns',
    type: 'TreeColumnDef<TData>[]',
    desc: 'Required. Columns shared by every depth. No expand column is prepended.',
  },
  {
    prop: 'data',
    type: 'TData[]',
    desc: 'Required. Root nodes only — fully materialized, not lazy-fetched.',
  },
  {
    prop: 'getRowId',
    type: '(node: TData) => string',
    desc: 'Required. Stable node identity, unique across the whole tree.',
  },
  {
    prop: 'getSubRows',
    type: '(node: TData) => TData[] | undefined',
    desc: "Required. A node's children — called again on whatever it returns.",
  },
  {
    prop: 'indentColumnId',
    type: 'string',
    desc: 'Required. Column id whose cell renders the indentation and expand chevron.',
  },
  { prop: 'indentSize', type: 'number', desc: 'Pixels of indentation per depth level (20).' },
  {
    prop: 'expanded',
    type: 'Record<string, boolean>',
    desc: 'Controlled expansion state. Omit for internally managed expansion.',
  },
  {
    prop: 'onExpandedChange',
    type: '(updater) => void',
    desc: 'Fires when expansion changes.',
  },
  {
    prop: 'defaultAllExpanded',
    type: 'boolean',
    desc: 'Expands every node with children on first render (false). Ignored once expansion is controlled.',
  },
  {
    prop: 'getRowCanExpand',
    type: '(node: TData) => boolean',
    desc: 'Extra veto beyond emptiness — permissions, node type.',
  },
  {
    prop: 'sortBy',
    type: 'SortingState',
    desc: 'Controlled sort state. Sorts siblings within each parent, not the flattened list.',
  },
  {
    prop: 'onUpdateSortBy',
    type: 'OnChangeFn<SortingState>',
    desc: 'Fired when the sort changes.',
  },
  {
    prop: 'manualSorting',
    type: 'boolean',
    desc: 'Set when the server already sorted (false).',
  },
  {
    prop: 'searchColumn',
    type: 'string',
    desc: 'Column id to filter on — renders the toolbar search box. A match keeps its ancestors visible.',
  },
  {
    prop: 'searchPlaceholder',
    type: 'string',
    desc: "Placeholder and accessible label for the search box ('Search...').",
  },
  {
    prop: 'searchMaxLength',
    type: 'number',
    desc: 'Longest term the search box accepts; set it to the server’s limit so a longer one cannot be typed.',
  },
  {
    prop: 'onSearchChange',
    type: '(value: string) => void',
    desc: 'Supply to drive search server-side: keystrokes are reported and local filtering stops.',
  },
  {
    prop: 'searchValue',
    type: 'string',
    desc: 'Controlled search box value — pair with onSearchChange to drive the input from outside.',
  },
  {
    prop: 'filters',
    type: 'React.ReactNode',
    desc: 'Extra filter controls, rendered next to the search input.',
  },
  {
    prop: 'filterChips',
    type: 'React.ReactNode',
    desc: 'Active-filter chips, rendered on their own row below the toolbar.',
  },
  {
    prop: 'actions',
    type: 'React.ReactNode',
    desc: 'Extra toolbar content, right-aligned beside the built-in controls.',
  },
  {
    prop: 'showViewOptions',
    type: 'boolean',
    desc: 'Renders the Columns visibility menu (false).',
  },
  {
    prop: 'showExpandAll',
    type: 'boolean',
    desc: 'Renders one Expand/Collapse button over the whole tree — there is no page to scope it to (false).',
  },
  {
    prop: 'columnFilters',
    type: 'ColumnFiltersState',
    desc: 'Controlled column filters. Omit for internally managed filtering.',
  },
  {
    prop: 'onColumnFiltersChange',
    type: 'OnChangeFn<ColumnFiltersState>',
    desc: 'Fires when column filters change.',
  },
  {
    prop: 'manualFiltering',
    type: 'boolean',
    desc: 'Set when the server already filtered (false).',
  },
  {
    prop: 'columnVisibility',
    type: 'VisibilityState',
    desc: 'Controlled column visibility. Omit for internally managed visibility.',
  },
  {
    prop: 'onColumnVisibilityChange',
    type: 'OnChangeFn<VisibilityState>',
    desc: 'Fires when a column is shown or hidden.',
  },
  {
    prop: 'selectedRowId',
    type: 'string',
    desc: 'The single selected node id, keyed by getRowId. Not TanStack row selection.',
  },
  {
    prop: 'onSelectRow',
    type: '(node: TData) => void',
    desc: 'Fired on click, or Enter/Space on a leaf or an already-expanded parent.',
  },
  {
    prop: 'getRowClassName',
    type: '(node: TData) => string | undefined',
    desc: "Extra classes for one row's <tr>, e.g. a background tint per node kind. Applied after selection styling — use ! to guarantee precedence.",
  },
  {
    prop: 'onRowClick',
    type: '(node: TData) => void',
    desc: 'Fired for a leaf on click or Enter/Space; an expandable row toggles instead. Ignored when onSelectRow is also given.',
  },
  { prop: 'isLoading', type: 'boolean', desc: 'Renders skeleton rows in place of data.' },
  { prop: 'loadingRowCount', type: 'number', desc: 'Skeleton row count when isLoading (20).' },
  { prop: 'emptyMessage', type: 'string', desc: "Shown when data is empty ('No results.')." },
  { prop: 'className', type: 'string', desc: 'Extra classes on the outer wrapper.' },
  {
    prop: 'maxBodyHeight',
    type: "'viewport' | string | number",
    desc: "'viewport' fills the height left below the table; a CSS length or px number caps it there instead. Unbounded when absent.",
  },
] as const;

type DocumentedProp = (typeof PROP_ROWS)[number]['prop'];
type UndocumentedProp = Exclude<keyof DimeTreeTableProps<ProductCategory>, DocumentedProp>;
// [T] extends [never] rather than T extends never: a naked type parameter distributes, so a union
// would resolve to never and accept true, making this assertion decorative.
const _allPropsDocumented: [UndocumentedProp] extends [never] ? true : UndocumentedProp = true;
void _allPropsDocumented;

function TreeTableDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Tree Table</h1>
      <p className="mb-8 text-muted-foreground">
        Arbitrary-depth tree table. Same-column, indented child rows in one grid via TanStack's{' '}
        <code className="font-mono">getSubRows</code> — a category tree, a BOM explosion, any
        self-referential hierarchy of unknown depth. Reach for{' '}
        <code className="font-mono">DimeNestedTable</code> instead when the data is genuinely two
        levels deep and the child table's columns differ from the parent's.
      </p>
      <p className="mb-8 text-muted-foreground">
        Fully materialized, not lazy: <code className="font-mono">data</code> is root nodes only,
        and <code className="font-mono">getSubRows</code> is called again on whatever it returns,
        all the way down. The whole tree below is four levels deep (Flower → Sativa → Sour Diesel →
        pack size) to actually demonstrate arbitrary depth rather than "two levels plus one more."
      </p>

      <DocsSection title="Basic expand and collapse">
        <BasicDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          The indentation and chevron live inside the <code className="font-mono">name</code> column
          (<code className="font-mono">indentColumnId=&quot;name&quot;</code>) — there is no
          separate expand column to prepend. &ldquo;Concentrates&rdquo; has no children, so it
          renders a same-width spacer instead of a chevron, keeping its value aligned with its
          expandable siblings.
        </p>
      </DocsSection>

      <DocsSection title="Expanded by default">
        <DefaultAllExpandedDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">defaultAllExpanded</code> opens every node with children on
          first render — a one-time seed for uncontrolled expansion, not a continuous sync. Once you
          pass <code className="font-mono">expanded</code>/
          <code className="font-mono">onExpandedChange</code> to control expansion yourself, seed
          the controlled value instead.
        </p>
      </DocsSection>

      <DocsSection title="Sortable columns">
        <SortableDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          Sorting sorts <strong>siblings within each parent</strong>, not the flattened row list —
          try sorting by Category here and notice each level re-sorts independently under its own
          parent rather than the whole tree collapsing into one flat alphabetical order. Put{' '}
          <code className="font-mono">DataTableColumnHeader</code> in a column&apos;s{' '}
          <code className="font-mono">header</code> for the sort menu, same as{' '}
          <code className="font-mono">DimeDataTable</code>.
        </p>
      </DocsSection>

      <DocsSection title="Search keeps a match's ancestors visible">
        <SearchDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          Try searching &ldquo;Diesel&rdquo;. The match and its full ancestor chain — Flower →
          Sativa → Sour Diesel — stay visible, rather than the search collapsing to an orphaned row
          with no path to it. This is the client default (
          <code className="font-mono">onSearchChange</code> omitted); pass it to drive search
          server-side instead.
        </p>
      </DocsSection>

      <DocsSection title="Toolbar: search, Columns, expand all">
        <ToolbarDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">showExpandAll</code> opens or closes the{' '}
          <strong>whole tree</strong> — there is no page to scope it to, since{' '}
          <code className="font-mono">DimeTreeTable</code> has no row pagination (a parent on one
          page and its child on another would not be a coherent tree view).{' '}
          <code className="font-mono">showViewOptions</code> adds the Columns menu; label columns
          via <code className="font-mono">meta.label</code>.
        </p>
      </DocsSection>

      <DocsSection title="Single-node selection (category picker)">
        <PickerDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">selectedRowId</code>/
          <code className="font-mono">onSelectRow</code> is a single selected node, not TanStack row
          selection — exactly what a category or BOM node picker needs. Click a row, or press
          Enter/Space on a leaf or an already-expanded parent, to select it.
        </p>
      </DocsSection>

      <DocsSection title="Loading">
        <LoadingDemo />
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">isLoading</code> replaces rows with skeletons. Set{' '}
          <code className="font-mono">loadingRowCount</code> to match your expected row count.
        </p>
      </DocsSection>

      <DocsSection title="Props">
        <DimeTable className="w-full text-sm">
          <DimeTableHeader>
            <DimeTableRow className="hover:bg-transparent">
              <DimeTableHead>Prop</DimeTableHead>
              <DimeTableHead>Type</DimeTableHead>
              <DimeTableHead>Description</DimeTableHead>
            </DimeTableRow>
          </DimeTableHeader>
          <DimeTableBody>
            {PROP_ROWS.map((row) => (
              <DimeTableRow key={row.prop}>
                <DimeTableCell className="font-mono text-xs">{row.prop}</DimeTableCell>
                <DimeTableCell className="font-mono text-xs text-muted-foreground">
                  {row.type}
                </DimeTableCell>
                <DimeTableCell className="text-muted-foreground">{row.desc}</DimeTableCell>
              </DimeTableRow>
            ))}
          </DimeTableBody>
        </DimeTable>
      </DocsSection>
    </div>
  );
}
